<template>
    <b-modal id="scheduleFormModal" :title="isEditing ? 'Edit Schedule' : 'Create Schedule'" size="lg" scrollable
        no-fade @show="onModalShow" @hidden="onModalHidden">
        <b-form @submit.prevent="saveSchedule">
            <b-row>
                <b-col cols="12" md="6">
                    <b-form-group label="Schedule Name" label-for="schedule-name">
                        <b-form-input id="schedule-name" v-model="schedule.name"
                            placeholder="e.g., Daily Sales Report" required />
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                    <b-form-group label="Export Format" label-for="export-format">
                        <b-form-select id="export-format" v-model="schedule.exportFormat" :options="exportFormats"
                            required />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-form-group v-if="schedule.exportFormat === 'Excel'">
                <b-form-checkbox v-model="schedule.includeCoverSheet">
                    Include Cover Sheet
                </b-form-checkbox>
            </b-form-group>

            <hr>

            <h6 class="mb-3">Schedule</h6>
            <CronScheduleEditor v-model="schedule.schedule" />

            <hr>

            <h6 class="mb-3">Delivery Configuration</h6>
            <b-row>
                <b-col cols="12" md="4">
                    <b-form-group label="Delivery Type" label-for="delivery-type">
                        <b-form-select id="delivery-type" v-model="schedule.deliveryType" :options="deliveryTypes"
                            @change="onDeliveryTypeChange" />
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="8">
                    <b-form-group label="Delivery Configuration" label-for="delivery-config">
                        <b-form-select id="delivery-config" v-model="schedule.deliveryConfigId"
                            :options="deliveryConfigOptions">
                            <template #first>
                                <b-form-select-option :value="null">-- Select Configuration --
                                </b-form-select-option>
                                <b-form-select-option :value="-1">+ Create New Configuration...
                                </b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-form-group>
                </b-col>
            </b-row>

            <!-- New Config Form -->
            <div v-if="schedule.deliveryConfigId === -1">
                <DeliveryConfigSftp v-if="schedule.deliveryType === 'SFTP'" v-model="newDeliveryConfig"
                    :is-editing="false" />
                <DeliveryConfigS3 v-else-if="schedule.deliveryType === 'S3'" v-model="newDeliveryConfig"
                    :is-editing="false" />

                <div class="d-flex justify-content-end mt-2">
                    <b-button variant="success" size="sm" @click="saveNewConfig" :disabled="savingConfig">
                        <b-spinner v-if="savingConfig" small class="mr-1" />
                        Save Configuration
                    </b-button>
                </div>
            </div>

            <b-row class="mt-3">
                <b-col cols="12" md="6">
                    <b-form-group label="Destination Path" label-for="destination-path">
                        <b-form-input id="destination-path" v-model="schedule.destinationPath"
                            placeholder="/exports/reports" />
                        <small class="text-muted">Remote folder path (SFTP) or S3 prefix</small>
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                    <b-form-group label="File Name Pattern" label-for="filename-pattern">
                        <b-form-input id="filename-pattern" v-model="schedule.fileNamePattern"
                            placeholder="{DashboardName}_{Date}" />
                        <small class="text-muted">
                            Placeholders: {DashboardName}, {DashboardId}, {Date}, {DateTime}, {Timestamp}
                        </small>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-form-group>
                <b-form-checkbox v-model="schedule.enabled">
                    <strong>Enabled</strong> - Schedule will execute automatically
                </b-form-checkbox>
            </b-form-group>
        </b-form>

        <template #modal-footer>
            <b-button variant="secondary" @click="closeModal">
                Cancel
            </b-button>
            <b-button variant="primary" @click="saveSchedule" :disabled="saving">
                <b-spinner v-if="saving" small class="mr-1" />
                {{ saving ? 'Saving...' : (isEditing ? 'Update Schedule' : 'Create Schedule') }}
            </b-button>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Vue, Prop } from "vue-property-decorator";
import CronScheduleEditor from "./CronScheduleEditor.vue";
import DeliveryConfigSftp from "./DeliveryConfigSftp.vue";
import DeliveryConfigS3 from "./DeliveryConfigS3.vue";
import {
    ReportExportSchedule,
    ReportExportScheduleRequest,
    SftpConfig,
    S3Config,
    SftpConfigRequest,
    S3ConfigRequest,
    reportExportService
} from "@/services/reportExportService";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    components: {
        CronScheduleEditor,
        DeliveryConfigSftp,
        DeliveryConfigS3
    }
})
export default class ScheduleFormModal extends Vue {
    @Prop({ required: true }) dashboardId!: string;
    @Prop({ default: "" }) dashboardTitle!: string;
    @Prop({ default: null }) editingSchedule!: ReportExportSchedule | null;

    private saving = false;
    private savingConfig = false;

    private schedule: ReportExportScheduleRequest = this.getEmptySchedule();

    private sftpConfigs: SftpConfig[] = [];
    private s3Configs: S3Config[] = [];
    private newDeliveryConfig: SftpConfigRequest | S3ConfigRequest = {};

    private exportFormats = [
        { value: "Excel", text: "Excel (.xlsx)" },
        { value: "CSV", text: "CSV (.csv)" },
        { value: "PipeDelimited", text: "Pipe Delimited (.txt)" }
    ];

    private deliveryTypes = [
        { value: "SFTP", text: "SFTP" },
        { value: "S3", text: "Amazon S3" }
    ];

    get isEditing(): boolean {
        return this.editingSchedule !== null;
    }

    get deliveryConfigOptions() {
        if (this.schedule.deliveryType === "SFTP") {
            return this.sftpConfigs.map(c => ({ value: c.id, text: c.name }));
        } else {
            return this.s3Configs.map(c => ({ value: c.id, text: c.name }));
        }
    }

    private getEmptySchedule(): ReportExportScheduleRequest {
        return {
            name: "",
            dashboardId: this.dashboardId || "",
            schedule: "0 6 * * *",
            exportFormat: "Excel",
            includeCoverSheet: false,
            enabled: true,
            deliveryType: "SFTP",
            deliveryConfigId: undefined,
            destinationPath: "",
            fileNamePattern: "{DashboardName}_{Date}"
        };
    }

    async onModalShow() {
        await this.loadDeliveryConfigs();

        if (this.editingSchedule) {
            this.schedule = {
                name: this.editingSchedule.name,
                dashboardId: this.editingSchedule.dashboardId,
                visualizationId: this.editingSchedule.visualizationId,
                schedule: this.editingSchedule.schedule,
                exportFormat: this.editingSchedule.exportFormat,
                includeCoverSheet: this.editingSchedule.includeCoverSheet,
                enabled: this.editingSchedule.enabled,
                deliveryType: this.editingSchedule.deliveryType,
                deliveryConfigId: this.editingSchedule.deliveryConfigId,
                destinationPath: this.editingSchedule.destinationPath,
                fileNamePattern: this.editingSchedule.fileNamePattern
            };
        } else {
            this.schedule = this.getEmptySchedule();
            this.schedule.dashboardId = this.dashboardId;
            if (this.dashboardTitle) {
                this.schedule.name = `${this.dashboardTitle} Export`;
            }
        }
    }

    onModalHidden() {
        this.schedule = this.getEmptySchedule();
        this.newDeliveryConfig = {};
    }

    private async loadDeliveryConfigs() {
        try {
            [this.sftpConfigs, this.s3Configs] = await Promise.all([
                reportExportService.listSftpConfigs(),
                reportExportService.listS3Configs()
            ]);
        } catch (error) {
            console.error("Failed to load delivery configs:", error);
        }
    }

    private onDeliveryTypeChange() {
        this.schedule.deliveryConfigId = undefined;
        this.newDeliveryConfig = {};
    }

    private async saveNewConfig() {
        this.savingConfig = true;
        try {
            if (this.schedule.deliveryType === "SFTP") {
                const config = await reportExportService.createSftpConfig(this.newDeliveryConfig as SftpConfigRequest);
                this.sftpConfigs.push(config);
                this.schedule.deliveryConfigId = config.id;
            } else {
                const config = await reportExportService.createS3Config(this.newDeliveryConfig as S3ConfigRequest);
                this.s3Configs.push(config);
                this.schedule.deliveryConfigId = config.id;
            }
            this.newDeliveryConfig = {};
            this.$notification(NotificationOptions.success("Configuration saved successfully"));
        } catch (error: any) {
            this.$notification(NotificationOptions.error(error.response?.data?.message || "Failed to save configuration"));
        } finally {
            this.savingConfig = false;
        }
    }

    private async saveSchedule() {
        if (!this.schedule.name || !this.schedule.schedule) {
            this.$notification(NotificationOptions.error("Please fill in all required fields"));
            return;
        }

        if (this.schedule.deliveryConfigId === -1 || !this.schedule.deliveryConfigId) {
            this.$notification(NotificationOptions.error("Please select or create a delivery configuration"));
            return;
        }

        this.saving = true;
        try {
            if (this.isEditing && this.editingSchedule) {
                await reportExportService.updateSchedule(this.editingSchedule.id, this.schedule);
                this.$notification(NotificationOptions.success("Schedule updated successfully"));
            } else {
                await reportExportService.createSchedule(this.schedule);
                this.$notification(NotificationOptions.success("Schedule created successfully"));
            }

            this.$emit("saved");
            this.closeModal();
        } catch (error: any) {
            this.$notification(NotificationOptions.error(error.response?.data?.message || "Failed to save schedule"));
        } finally {
            this.saving = false;
        }
    }

    private closeModal() {
        this.$bvModal.hide("scheduleFormModal");
    }
}
</script>

<style scoped>
h6 {
    color: #333;
    font-weight: 600;
}

hr {
    margin: 1.5rem 0;
}
</style>
